import { supabase } from '../lib/supabase';

// ─── PLAN DE TRABAJO SERVICE ─────────────────────────────────────────────────

// Normaliza campos UUID: convierte "" o undefined a null para PostgreSQL
const uuidOrNull = (value) => {
  if (!value || value === '') return null;
  return value;
};

// Limpia un objeto de updates eliminando undefined y normalizando UUIDs vacíos
const cleanUpdates = (obj) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    const uuidFields = [
      'empleado_id', 'configuracion_origen_id', 'empleado_preferente_id',
      'plan_trabajo_id', 'plan_trabajo_pieza_id', 'pieza_id', 'producto_id',
    ];
    if (uuidFields?.includes(key)) {
      cleaned[key] = uuidOrNull(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

const getWeekRange = (date) => {
  // Accept Date object or YYYY-MM-DD string — always use local date arithmetic
  let d;
  if (date instanceof Date) {
    d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  } else if (typeof date === 'string' && date) {
    const parts = date?.split('-');
    d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  } else {
    const now = new Date();
    d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  const dow = d?.getDay(); // 0=Sun, 1=Mon..6=Sat
  const diffToMon = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday?.setDate(d?.getDate() + diffToMon);
  const saturday = new Date(monday);
  saturday?.setDate(monday?.getDate() + 5); // Mon + 5 days = Sat (6 columns: Mon–Sat)
  return {
    start: toDateKey(monday),
    end: toDateKey(saturday),
  };
};

// Timezone-safe: Date → YYYY-MM-DD using local fields (no UTC shift)
const toDateKey = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date?.getTime())) return null;
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  const dd = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// ── Trabajos (plan_trabajo) ───────────────────────────────────────────────────

export const planTrabajoService = {
  // Get all trabajos for a week with joins — ordered by orden_dia
  getByWeek: async (weekDate) => {
    const { start, end } = getWeekRange(weekDate);
    const { data, error } = await supabase?.from('plan_trabajo')?.select(`
        *,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre),
        piezas:plan_trabajo_piezas(*, empleado:empleados(id, nombre))
      `)
      ?.gte('fecha', start)
      ?.lte('fecha', end)
      ?.order('fecha', { ascending: true })
      ?.order('orden_dia', { ascending: true })
      ?.order('created_at', { ascending: true });
    if (error) { console.error('[planTrabajoService.getByWeek]', error); throw error; }
    return data || [];
  },

  // Get trabajos without a date (bandeja sin programar)
  getUnscheduled: async () => {
    const { data, error } = await supabase?.from('plan_trabajo')?.select(`
        *,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre),
        piezas:plan_trabajo_piezas(*, empleado:empleados(id, nombre))
      `)
      ?.is('fecha', null)
      ?.neq('estado', 'COMPLETADO')
      ?.neq('estado', 'CANCELADO')
      ?.order('created_at', { ascending: false });
    if (error) { console.error('[planTrabajoService.getUnscheduled]', error); throw error; }
    return data || [];
  },

  // Get trabajos for today ordered by orden_dia (worker mode)
  getToday: async () => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const { data, error } = await supabase?.from('plan_trabajo')?.select(`
        *,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre),
        piezas:plan_trabajo_piezas(*, empleado:empleados(id, nombre))
      `)
      ?.eq('fecha', today)
      ?.neq('estado', 'COMPLETADO')
      ?.neq('estado', 'CANCELADO')
      ?.order('orden_dia', { ascending: true })
      ?.order('created_at', { ascending: true });
    if (error) { console.error('[planTrabajoService.getToday]', error); throw error; }
    return data || [];
  },

  // Create a new trabajo and auto-generate piezas
  create: async (trabajoData) => {
    const { cantidad_piezas = 1, ...rest } = trabajoData;
    if (!rest?.cliente_id) throw new Error('cliente_id es obligatorio');
    if (!rest?.producto_id) throw new Error('producto_id es obligatorio');
    if (cantidad_piezas < 1) throw new Error('cantidad_piezas debe ser >= 1');

    // Calcular orden_dia si tiene fecha
    let orden_dia = rest?.orden_dia;
    if (rest?.fecha && orden_dia === undefined) {
      const { data: existing } = await supabase
        ?.from('plan_trabajo')
        ?.select('orden_dia')
        ?.eq('fecha', rest?.fecha)
        ?.order('orden_dia', { ascending: false })
        ?.limit(1);
      const maxOrden = existing?.[0]?.orden_dia ?? -1;
      orden_dia = maxOrden + 1;
    }

    const payload = cleanUpdates({
      ...rest,
      cantidad_piezas,
      cantidad_recibida: 0,
      recepcion_completa: false,
      ...(orden_dia !== undefined ? { orden_dia } : {}),
    });

    const { data: trabajo, error } = await supabase?.from('plan_trabajo')
      ?.insert(payload)
      ?.select()?.single();
    if (error) { console.error('[planTrabajoService.create]', error); throw error; }

    // NOTA: Las piezas son creadas automáticamente por el trigger SQL de Supabase
    return trabajo;
  },

  // Duplicate a trabajo for another day, preserving client, product and operational settings.
  // The copy always starts clean and gets a fresh set of pieces from the database trigger.
  duplicate: async (id, fechaDestino) => {
    const { data: original, error: fetchError } = await supabase?.from('plan_trabajo')
      ?.select('*')?.eq('id', id)?.single();
    if (fetchError) throw fetchError;
    if (!original) throw new Error('Trabajo original no encontrado');
    if (!original?.cliente_id || !original?.producto_id) {
      throw new Error('El trabajo original no tiene cliente o producto');
    }

    const {
      id: _id,
      created_at: _createdAt,
      updated_at: _updatedAt,
      orden_dia: _ordenDia,
      estado: _estado,
      cantidad_recibida: _cantidadRecibida,
      recepcion_completa: _recepcionCompleta,
      ...base
    } = original;

    let orden_dia = null;
    if (fechaDestino) {
      const { data: existing } = await supabase
        ?.from('plan_trabajo')
        ?.select('orden_dia')
        ?.eq('fecha', fechaDestino)
        ?.order('orden_dia', { ascending: false })
        ?.limit(1);
      orden_dia = (existing?.[0]?.orden_dia ?? -1) + 1;
    }

    const payload = cleanUpdates({
      ...base,
      fecha: fechaDestino || null,
      orden_dia,
      estado: 'PENDIENTE',
      cantidad_recibida: 0,
      recepcion_completa: false,
    });

    const { data, error } = await supabase?.from('plan_trabajo')
      ?.insert(payload)?.select()?.single();
    if (error) { console.error('[planTrabajoService.duplicate]', error); throw error; }
    return data;
  },

  // Update a trabajo — normaliza UUIDs vacíos a null antes de enviar
  update: async (id, updates) => {
    const cleanedUpdates = cleanUpdates(updates);
    const { data, error } = await supabase?.from('plan_trabajo')
      ?.update({ ...cleanedUpdates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) { console.error('[planTrabajoService.update]', error); throw error; }
    return data;
  },

  // Full edit of a trabajo (handles pieza count changes)
  edit: async (id, updates) => {
    const { cantidad_piezas: newCount, ...rest } = updates;
    const cleanedRest = cleanUpdates(rest);
    const updated = await planTrabajoService?.update(id, { ...cleanedRest, ...(newCount !== undefined ? { cantidad_piezas: newCount } : {}) });

    if (newCount !== undefined) {
      const { data: currentPiezas, error: fetchError } = await supabase?.from('plan_trabajo_piezas')
        ?.select('*')?.eq('trabajo_id', id)?.order('numero_pieza', { ascending: true });
      if (fetchError) throw fetchError;

      const currentCount = currentPiezas?.length || 0;

      if (newCount > currentCount) {
        const toAdd = Array.from({ length: newCount - currentCount }, (_, i) => ({
          trabajo_id: id,
          numero_pieza: currentCount + i + 1,
          producto_id: uuidOrNull(cleanedRest?.producto_id) || uuidOrNull(updated?.producto_id),
          estado: 'PENDIENTE',
        }));
        const { error } = await supabase?.from('plan_trabajo_piezas')?.insert(toAdd);
        if (error) throw error;
      } else if (newCount < currentCount) {
        const pendientes = currentPiezas?.filter(p => p?.estado === 'PENDIENTE');
        const procesadas = currentPiezas?.filter(p => p?.estado !== 'PENDIENTE');
        if (procesadas?.length > 0 && newCount < procesadas?.length) {
          throw new Error(`No se pueden eliminar piezas en proceso o completadas. Hay ${procesadas?.length} piezas activas.`);
        }
        const toRemove = pendientes?.slice(-(currentCount - newCount))?.map(p => p?.id);
        if (toRemove?.length > 0) {
          const { error } = await supabase?.from('plan_trabajo_piezas')?.delete()?.in('id', toRemove);
          if (error) throw error;
        }
      }
    }

    return updated;
  },

  // Move trabajo to a different date (null = sin programar)
  moveToDate: async (id, newDate) => {
    const updates = { fecha: newDate || null, updated_at: new Date()?.toISOString() };
    // If moving to null (unscheduled), also clear orden_dia
    if (!newDate) {
      updates.orden_dia = null;
    } else {
      // Assign orden_dia at end of target day
      const { data: existing } = await supabase
        ?.from('plan_trabajo')
        ?.select('orden_dia')
        ?.eq('fecha', newDate)
        ?.neq('id', id)
        ?.order('orden_dia', { ascending: false })
        ?.limit(1);
      const maxOrden = existing?.[0]?.orden_dia ?? -1;
      updates.orden_dia = maxOrden + 1;
    }
    const { data, error } = await supabase?.from('plan_trabajo')
      ?.update(updates)
      ?.eq('id', id)?.select()?.single();
    if (error) { console.error('[planTrabajoService.moveToDate]', error); throw error; }
    return data;
  },

  /**
   * Moves a trabajo to a specific date and inserts it at a given position.
   * Recalculates orden_dia on both source and destination days without gaps or duplicates.
   *
   * @param {string} trabajoId - UUID of the trabajo to move
   * @param {string|null} fechaDestino - YYYY-MM-DD target date, or null for unscheduled
   * @param {number} posicionDestino - 0-based index where to insert in destination day
   */
  moveTrabajoToDateAndPosition: async (trabajoId, fechaDestino, posicionDestino) => {
    // 1. Fetch the trabajo to know its current date
    const { data: trabajo, error: fetchErr } = await supabase
      ?.from('plan_trabajo')
      ?.select('id, fecha, orden_dia')
      ?.eq('id', trabajoId)
      ?.single();
    if (fetchErr) throw fetchErr;

    const fechaOrigen = trabajo?.fecha || null;

    // 2. If moving to null (sin programar)
    if (!fechaDestino) {
      const { error: updateErr } = await supabase
        ?.from('plan_trabajo')
        ?.update({ fecha: null, orden_dia: null, updated_at: new Date()?.toISOString() })
        ?.eq('id', trabajoId);
      if (updateErr) throw updateErr;

      // Recalculate source day if it had a date
      if (fechaOrigen) {
        const { data: srcItems } = await supabase
          ?.from('plan_trabajo')
          ?.select('id, orden_dia')
          ?.eq('fecha', fechaOrigen)
          ?.neq('id', trabajoId)
          ?.order('orden_dia', { ascending: true });
        const reorderUpdates = (srcItems || [])?.map((t, i) =>
          supabase?.from('plan_trabajo')?.update({ orden_dia: i })?.eq('id', t?.id)
        );
        await Promise.all(reorderUpdates);
      }
      return;
    }

    // 3. Fetch destination day items (excluding the moving trabajo)
    const { data: destItems } = await supabase
      ?.from('plan_trabajo')
      ?.select('id, orden_dia')
      ?.eq('fecha', fechaDestino)
      ?.neq('id', trabajoId)
      ?.order('orden_dia', { ascending: true });

    const destList = [...(destItems || [])];

    // 4. Insert at position
    const clampedPos = Math.max(0, Math.min(posicionDestino, destList?.length));
    destList?.splice(clampedPos, 0, { id: trabajoId });

    // 5. Update destination day with new orden_dia values
    const destUpdates = destList?.map((t, i) =>
      supabase?.from('plan_trabajo')?.update({
        orden_dia: i,
        ...(t?.id === trabajoId ? { fecha: fechaDestino, updated_at: new Date()?.toISOString() } : {}),
      })?.eq('id', t?.id)
    );
    await Promise.all(destUpdates);

    // 6. Recalculate source day (if different from destination)
    if (fechaOrigen && fechaOrigen !== fechaDestino) {
      const { data: srcItems } = await supabase
        ?.from('plan_trabajo')
        ?.select('id, orden_dia')
        ?.eq('fecha', fechaOrigen)
        ?.neq('id', trabajoId)
        ?.order('orden_dia', { ascending: true });
      const srcUpdates = (srcItems || [])?.map((t, i) =>
        supabase?.from('plan_trabajo')?.update({ orden_dia: i })?.eq('id', t?.id)
      );
      await Promise.all(srcUpdates);
    } else if (!fechaOrigen) {
      // Was unscheduled — no source day to recalculate
    }
  },

  // Update estado
  updateEstado: async (id, estado) => {
    return planTrabajoService?.update(id, { estado });
  },

  // Update orden_dia for multiple trabajos (drag-and-drop reorder)
  reorderDay: async (trabajosOrdenados) => {
    const updates = trabajosOrdenados?.map(({ id, orden_dia }) =>
      supabase?.from('plan_trabajo')?.update({ orden_dia })?.eq('id', id)
    );
    const results = await Promise.all(updates);
    const failed = results?.find(r => r?.error);
    if (failed?.error) { console.error('[planTrabajoService.reorderDay]', failed?.error); throw failed?.error; }
  },

  // Partial reception: increment cantidad_recibida
  marcarRecibido: async (trabajoId, cantidadRecibida) => {
    const { data: trabajo, error: fetchErr } = await supabase?.from('plan_trabajo')
      ?.select('cantidad_piezas, cantidad_recibida, estado')?.eq('id', trabajoId)?.single();
    if (fetchErr) throw fetchErr;

    const nuevaCantidad = cantidadRecibida ?? ((trabajo?.cantidad_recibida || 0) + 1);
    const recepcionCompleta = nuevaCantidad >= (trabajo?.cantidad_piezas || 1);

    return planTrabajoService?.update(trabajoId, {
      cantidad_recibida: nuevaCantidad,
      recepcion_completa: recepcionCompleta,
    });
  },

  // Marcar pieza individual como recibida y recalcular cantidad_recibida desde piezas
  marcarPiezaRecibida: async (piezaId, recibida) => {
    const { data: pieza, error: fetchErr } = await supabase?.from('plan_trabajo_piezas')
      ?.select('trabajo_id')?.eq('id', piezaId)?.single();
    if (fetchErr) throw fetchErr;

    const { error: updateErr } = await supabase?.from('plan_trabajo_piezas')
      ?.update({ recibida: !!recibida, updated_at: new Date()?.toISOString() })
      ?.eq('id', piezaId);
    if (updateErr) throw updateErr;

    // Recalcular cantidad_recibida desde piezas reales
    const { data: allPiezas } = await supabase?.from('plan_trabajo_piezas')
      ?.select('recibida')?.eq('trabajo_id', pieza?.trabajo_id);
    const cantidadRecibida = (allPiezas || [])?.filter(p => p?.recibida)?.length;
    const total = allPiezas?.length || 0;
    await planTrabajoService?.update(pieza?.trabajo_id, {
      cantidad_recibida: cantidadRecibida,
      recepcion_completa: cantidadRecibida >= total,
    });
  },

  // Adjust piezas count (+1 / -1)
  adjustPiezas: async (trabajoId, newCount, productoId) => {
    if (newCount < 1) throw new Error('cantidad_piezas debe ser >= 1');

    const { data: currentPiezas, error: fetchError } = await supabase?.from('plan_trabajo_piezas')
      ?.select('*')?.eq('trabajo_id', trabajoId)?.order('numero_pieza', { ascending: true });
    if (fetchError) throw fetchError;

    const currentCount = currentPiezas?.length || 0;

    if (newCount > currentCount) {
      const toAdd = Array.from({ length: newCount - currentCount }, (_, i) => ({
        trabajo_id: trabajoId,
        numero_pieza: currentCount + i + 1,
        producto_id: uuidOrNull(productoId),
        estado: 'PENDIENTE',
      }));
      const { error } = await supabase?.from('plan_trabajo_piezas')?.insert(toAdd);
      if (error) throw error;
    } else if (newCount < currentCount) {
      const toRemove = currentPiezas?.filter(p => p?.estado === 'PENDIENTE')
        ?.slice(-(currentCount - newCount))?.map(p => p?.id);
      if (toRemove?.length > 0) {
        const { error } = await supabase?.from('plan_trabajo_piezas')?.delete()?.in('id', toRemove);
        if (error) throw error;
      }
    }

    return planTrabajoService?.update(trabajoId, { cantidad_piezas: newCount });
  },

  // Delete a trabajo and its piezas (only if no active piezas and no loncheados)
  delete: async (id) => {
    const { data: piezas, error: fetchErr } = await supabase?.from('plan_trabajo_piezas')
      ?.select('estado, loncheado_id')?.eq('trabajo_id', id);
    if (fetchErr) throw fetchErr;

    const activePiezas = piezas?.filter(p => p?.estado === 'EN_PROCESO' || p?.estado === 'COMPLETADA');
    if (activePiezas?.length > 0) throw new Error('TIENE_PRODUCCION');

    const conLoncheado = piezas?.filter(p => p?.loncheado_id);
    if (conLoncheado?.length > 0) throw new Error('TIENE_PRODUCCION');

    const { error: piezasError } = await supabase?.from('plan_trabajo_piezas')?.delete()?.eq('trabajo_id', id);
    if (piezasError) throw piezasError;
    const { error } = await supabase?.from('plan_trabajo')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Cancel a trabajo: SET estado = 'CANCELADO' — conserva piezas, loncheados y trazabilidad
  cancel: async (id) => {
    const { data, error } = await supabase?.from('plan_trabajo')
      ?.update({ estado: 'CANCELADO', updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()?.single();
    if (error) { console.error('[planTrabajoService.cancel]', error); throw error; }
    return data;
  },

  // Copy previous week's PUNTUAL trabajos to current week
  copyPreviousWeek: async (targetWeekDate) => {
    const { start: targetStart } = getWeekRange(targetWeekDate);
    const prevWeekDate = new Date(targetStart);
    prevWeekDate?.setDate(prevWeekDate?.getDate() - 7);
    const { start: prevStart, end: prevEnd } = getWeekRange(prevWeekDate);

    const { data: prevTrabajos, error } = await supabase?.from('plan_trabajo')?.select('*')?.gte('fecha', prevStart)?.lte('fecha', prevEnd)?.eq('tipo_trabajo', 'PUNTUAL');
    if (error) throw error;

    const results = [];
    for (const t of (prevTrabajos || [])) {
      const prevDate = new Date(t.fecha);
      const newDate = new Date(prevDate);
      newDate?.setDate(prevDate?.getDate() + 7);
      const { id, created_at, updated_at, ...rest } = t;
      results?.push(await planTrabajoService?.create({
        ...rest,
        fecha: newDate?.toISOString()?.split('T')?.[0],
        estado: 'PENDIENTE',
        cantidad_recibida: 0,
        recepcion_completa: false,
      }));
    }
    return results;
  },

  // ── RECURRENCIA ──────────────────────────────────────────────────────────────
  // Genera trabajos recurrentes para las próximas 4 semanas sin duplicar
  generateRecurrentes: async () => {
    // Obtener todas las configuraciones recurrentes activas
    const { data: configs, error: configErr } = await supabase
      ?.from('plan_trabajo_cliente_config')
      ?.select('*, cliente:clientes(id, nombre), producto:productos(id, nombre)')
      ?.eq('recurrente', true)
      ?.eq('activo', true);
    if (configErr) { console.error('[planTrabajoService.generateRecurrentes]', configErr); return; }

    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    // Ventana: hoy + 28 días (4 semanas)
    const windowEnd = new Date(today);
    windowEnd?.setDate(today?.getDate() + 28);

    for (const config of (configs || [])) {
      if (!config?.dia_semana || !config?.cliente_id || !config?.producto_id) continue;

      const frecuencia = config?.frecuencia || 'SEMANAL';
      const intervalDays = frecuencia === 'SEMANAL' ? 7 : frecuencia === 'BISEMANAL' ? 14 : 28;

      // Calcular fechas objetivo dentro de la ventana
      const targetDates = [];
      const cursor = new Date(today);

      // Avanzar al próximo día_semana (1=Lunes, 7=Domingo)
      const targetDow = config?.dia_semana; // 1=Mon ... 7=Sun
      const cursorDow = cursor?.getDay() === 0 ? 7 : cursor?.getDay();
      let daysUntilTarget = targetDow - cursorDow;
      if (daysUntilTarget < 0) daysUntilTarget += 7;
      if (daysUntilTarget === 0) daysUntilTarget = 0; // same day is valid

      cursor?.setDate(cursor?.getDate() + daysUntilTarget);

      while (cursor <= windowEnd) {
        targetDates?.push(cursor?.toISOString()?.split('T')?.[0]);
        cursor?.setDate(cursor?.getDate() + intervalDays);
      }

      if (!targetDates?.length) continue;

      // Verificar cuáles fechas ya tienen trabajo para este cliente+config
      const { data: existing } = await supabase
        ?.from('plan_trabajo')
        ?.select('fecha')
        ?.eq('cliente_id', config?.cliente_id)
        ?.eq('configuracion_origen_id', config?.id)
        ?.in('fecha', targetDates)
        ?.neq('estado', 'CANCELADO');

      const existingDates = new Set((existing || [])?.map(t => t?.fecha));

      // Crear solo los que faltan (idempotente)
      for (const fecha of targetDates) {
        if (existingDates?.has(fecha)) continue;
        try {
          await planTrabajoService?.create({
            cliente_id: config?.cliente_id,
            producto_id: uuidOrNull(config?.producto_id),
            empleado_id: uuidOrNull(config?.empleado_preferente_id),
            fecha,
            tipo_trabajo: 'RECURRENTE',
            cantidad_piezas: config?.cantidad_habitual || 1,
            tipo_corte: config?.tipo_corte || 'NORMAL',
            formato_corte: config?.formato_corte || '100G',
            quiere_huesos: config?.quiere_huesos || false,
            guardar_merma: config?.guardar_merma || false,
            guardar_recortes: config?.guardar_recortes || false,
            prioridad: config?.prioridad_habitual || 'NORMAL',
            observaciones: config?.observaciones_habituales || config?.observaciones || null,
            configuracion_origen_id: config?.id,
            estado: 'PENDIENTE',
          });
        } catch (err) {
          console.warn('[planTrabajoService.generateRecurrentes] skip:', fecha, err?.message);
        }
      }
    }
  },
};

// ── Piezas (plan_trabajo_piezas) ─────────────────────────────────────────────

export const planTrabajoPiezasService = {
  getByTrabajo: async (trabajoId) => {
    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.select('*, producto:productos(id, nombre), empleado:empleados(id, nombre)')
      ?.eq('trabajo_id', trabajoId)
      ?.order('numero_pieza', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // Asignar empleado a una pieza individual
  asignarEmpleado: async (piezaId, empleadoId) => {
    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.update({ empleado_id: uuidOrNull(empleadoId), updated_at: new Date()?.toISOString() })
      ?.eq('id', piezaId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Start a pieza: set EN_PROCESO, also update trabajo to EN_PROCESO
  iniciarPieza: async (piezaId) => {
    const { data: pieza, error: fetchErr } = await supabase?.from('plan_trabajo_piezas')
      ?.select('trabajo_id')?.eq('id', piezaId)?.single();
    if (fetchErr) throw fetchErr;

    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.update({ estado: 'EN_PROCESO', updated_at: new Date()?.toISOString() })
      ?.eq('id', piezaId)?.select()?.single();
    if (error) throw error;

    await planTrabajoService?.updateEstado(pieza?.trabajo_id, 'EN_PROCESO');
    return data;
  },

  // Start a pieza AND persist empleado_id to the piece row in Supabase
  // This ensures MIS PIEZAS, Vista Operario and filters by employee work correctly
  iniciarPiezaConEmpleado: async (piezaId, trabajoEmpleadoId) => {
    const { data: pieza, error: fetchErr } = await supabase?.from('plan_trabajo_piezas')
      ?.select('trabajo_id, empleado_id')?.eq('id', piezaId)?.single();
    if (fetchErr) throw fetchErr;

    // Resolve empleado: use pieza-level if set, otherwise inherit from trabajo
    const empleadoIdToSet = uuidOrNull(pieza?.empleado_id) || uuidOrNull(trabajoEmpleadoId) || null;

    const updatePayload = {
      estado: 'EN_PROCESO',
      updated_at: new Date()?.toISOString(),
    };
    // Only set empleado_id if pieza doesn't already have one and trabajo has one
    if (!pieza?.empleado_id && empleadoIdToSet) {
      updatePayload.empleado_id = empleadoIdToSet;
    }

    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.update(updatePayload)
      ?.eq('id', piezaId)?.select()?.single();
    if (error) throw error;

    await planTrabajoService?.updateEstado(pieza?.trabajo_id, 'EN_PROCESO');
    return data;
  },

  updateEstado: async (piezaId, estado, loncheadoId = null) => {
    const updates = { estado, updated_at: new Date()?.toISOString() };
    if (loncheadoId) updates.loncheado_id = loncheadoId;
    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.update(updates)?.eq('id', piezaId)?.select()?.single();
    if (error) throw error;

    // Recalculate trabajo estado from real piece rows
    const { data: allPiezas } = await supabase?.from('plan_trabajo_piezas')
      ?.select('estado')?.eq('trabajo_id', data?.trabajo_id);
    const allDone = allPiezas?.every(p => p?.estado === 'COMPLETADA');
    const anyInProcess = allPiezas?.some(p => p?.estado === 'EN_PROCESO');
    if (allDone) {
      await planTrabajoService?.updateEstado(data?.trabajo_id, 'COMPLETADO');
    } else if (anyInProcess || estado === 'EN_PROCESO') {
      await planTrabajoService?.updateEstado(data?.trabajo_id, 'EN_PROCESO');
    }
    return data;
  },

  // Link pieza to a loncheado record and mark COMPLETADA
  linkLoncheado: async (piezaId, loncheadoId) => {
    return planTrabajoPiezasService?.updateEstado(piezaId, 'COMPLETADA', loncheadoId);
  },

  // Get next pending pieza for a trabajo
  getNextPendiente: async (trabajoId) => {
    const { data, error } = await supabase?.from('plan_trabajo_piezas')
      ?.select('*')
      ?.eq('trabajo_id', trabajoId)
      ?.in('estado', ['PENDIENTE', 'EN_PROCESO'])
      ?.order('numero_pieza', { ascending: true })
      ?.limit(1)?.single();
    if (error && error?.code !== 'PGRST116') {
      console.error('[planTrabajoPiezasService.getNextPendiente]', error);
    }
    return data || null;
  },
};

// ── Incidencias (plan_trabajo_incidencias) ────────────────────────────────────

export const planTrabajoIncidenciasService = {
  TIPOS: [
    { value: 'FALTA_MERCANCIA', label: 'Falta mercancía' },
    { value: 'PIEZA_DEFECTUOSA', label: 'Pieza defectuosa' },
    { value: 'CLIENTE_CAMBIA', label: 'Cliente cambia condiciones' },
    { value: 'NO_TERMINA_HOY', label: 'No se termina hoy' },
    { value: 'OTRO', label: 'Otro' },
  ],

  create: async (incidenciaData) => {
    const { data, error } = await supabase?.from('plan_trabajo_incidencias')
      ?.insert({
        trabajo_id: incidenciaData?.trabajoId,
        pieza_id: uuidOrNull(incidenciaData?.piezaId),
        empleado_id: uuidOrNull(incidenciaData?.empleadoId),
        tipo: incidenciaData?.tipo || 'OTRO',
        descripcion: incidenciaData?.descripcion || null,
      })?.select()?.single();
    if (error) throw error;

    await planTrabajoService?.updateEstado(incidenciaData?.trabajoId, 'INCIDENCIA');
    return data;
  },

  getByTrabajo: async (trabajoId) => {
    const { data, error } = await supabase?.from('plan_trabajo_incidencias')
      ?.select('*, empleado:empleados(id, nombre)')
      ?.eq('trabajo_id', trabajoId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
};

// ── Cliente Config (plan_trabajo_cliente_config) ──────────────────────────────

export const clienteConfigService = {
  getAll: async () => {
    const { data, error } = await supabase?.from('plan_trabajo_cliente_config')?.select(`
        *,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre)
      `)?.eq('activo', true)?.order('created_at', { ascending: true });
    if (error) { console.error('[clienteConfigService.getAll]', error); throw error; }
    return data || [];
  },

  getByCliente: async (clienteId) => {
    const { data, error } = await supabase?.from('plan_trabajo_cliente_config')?.select(`
        *,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre)
      `)?.eq('cliente_id', clienteId)?.single();
    if (error && error?.code !== 'PGRST116') {
      console.error('[clienteConfigService.getByCliente]', error);
      throw error;
    }
    return data || null;
  },

  upsert: async (configData) => {
    if (!configData?.cliente_id || configData?.cliente_id === '') {
      throw new Error('cliente_id es obligatorio para guardar la configuración');
    }

    const payload = {
      ...(configData?.id ? { id: configData?.id } : {}),
      cliente_id: configData?.cliente_id,
      updated_at: new Date()?.toISOString(),
    };

    const optionalFields = {
      color: configData?.color,
      producto_id: configData?.producto_id !== undefined ? uuidOrNull(configData?.producto_id) : undefined,
      empleado_preferente_id: configData?.empleado_preferente_id !== undefined ? uuidOrNull(configData?.empleado_preferente_id) : undefined,
      tipo_corte: configData?.tipo_corte,
      formato_corte: configData?.formato_corte,
      quiere_huesos: configData?.quiere_huesos !== undefined ? !!configData?.quiere_huesos : undefined,
      guardar_merma: configData?.guardar_merma !== undefined ? !!configData?.guardar_merma : undefined,
      guardar_recortes: configData?.guardar_recortes !== undefined ? !!configData?.guardar_recortes : undefined,
      emplatado: configData?.emplatado !== undefined ? !!configData?.emplatado : undefined,
      recurrente: configData?.recurrente !== undefined ? !!configData?.recurrente : undefined,
      dia_semana: configData?.dia_semana,
      cantidad_habitual: configData?.cantidad_habitual,
      prioridad_habitual: configData?.prioridad_habitual,
      frecuencia: configData?.frecuencia,
      observaciones: configData?.observaciones,
      observaciones_habituales: configData?.observaciones_habituales,
      instrucciones: configData?.instrucciones,
      activo: configData?.activo,
    };

    for (const [key, value] of Object.entries(optionalFields)) {
      if (value !== undefined) payload[key] = value;
    }

    const { data, error } = await supabase?.from('plan_trabajo_cliente_config')
      ?.upsert(payload, { onConflict: 'cliente_id' })
      ?.select()?.single();
    if (error) { console.error('[clienteConfigService.upsert]', error); throw error; }
    return data;
  },

  buildTrabajoFromConfig: (config, overrides = {}) => {
    if (!config) return overrides;
    return {
      cliente_id: config?.cliente_id,
      producto_id: uuidOrNull(config?.producto_id),
      empleado_id: uuidOrNull(config?.empleado_preferente_id),
      tipo_trabajo: config?.recurrente ? 'RECURRENTE' : 'PUNTUAL',
      cantidad_piezas: config?.cantidad_habitual,
      tipo_corte: config?.tipo_corte,
      emplatado: config?.emplatado,
      formato_gramos: config?.formato_gramos,
      formato_corte: config?.formato_corte || 'ENTERO',
      quiere_huesos: config?.quiere_huesos || false,
      guardar_merma: config?.guardar_merma || false,
      guardar_recortes: config?.guardar_recortes || false,
      prioridad: config?.prioridad_habitual || 'NORMAL',
      observaciones: config?.observaciones_habituales || config?.observaciones || '',
      configuracion_origen_id: uuidOrNull(config?.id),
      ...overrides,
    };
  },
};

export { getWeekRange };

/**
 * Converts a local Date object to YYYY-MM-DD string WITHOUT UTC conversion.
 * Uses getFullYear/getMonth/getDate to avoid timezone shifts.
 */
function toLocalDateString(date) {
  if (!date || !(date instanceof Date) || isNaN(date?.getTime())) return null;
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  let d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export { toLocalDateString };

/**
 * Parses a YYYY-MM-DD string into a local Date object WITHOUT UTC conversion.
 * Uses new Date(year, month-1, day) to stay in local timezone.
 */
function parseLocalDate(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str?.split('-');
  if (parts?.length !== 3) return null;
  const y = parseInt(parts?.[0], 10);
  const m = parseInt(parts?.[1], 10) - 1;
  let d = parseInt(parts?.[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m, d);
}

export { parseLocalDate };

// ── EMPLEADO ACTUAL ───────────────────────────────────────────────────────────
// Obtiene el empleado vinculado al usuario autenticado usando user_id (NO email)
// Relación: auth.users.id → public.empleados.user_id

export const getCurrentEmpleado = async (authUserId) => {
  if (!authUserId) return null;
  const { data, error } = await supabase
    ?.from('empleados')
    ?.select('id, nombre, activo')
    ?.eq('user_id', authUserId)
    ?.eq('activo', true)
    ?.maybeSingle();
  if (error) {
    console.warn('[getCurrentEmpleado] Error:', error?.message);
    return null;
  }
  return data || null;
};

// ── SHARED PRELOAD CONTEXT ────────────────────────────────────────────────────
// Reutilizable desde Plan de Trabajo (EMPEZAR) y Registro de Piezas (Selector)
// Recibe un plan_trabajo_pieza_id y devuelve todos los datos necesarios para precargar el formulario

export const loadPlanTrabajoContext = async (piezaId) => {
  if (!piezaId) throw new Error('piezaId es obligatorio');

  // 1. Cargar pieza con joins
  const { data: pieza, error: piezaErr } = await supabase
    ?.from('plan_trabajo_piezas')
    ?.select(`
      *,
      producto:productos(id, nombre),
      empleado:empleados(id, nombre)
    `)
    ?.eq('id', piezaId)
    ?.single();
  if (piezaErr) throw piezaErr;
  if (!pieza) throw new Error('Pieza no encontrada');

  // 2. Cargar trabajo padre con joins
  const { data: trabajo, error: trabajoErr } = await supabase
    ?.from('plan_trabajo')
    ?.select(`
      *,
      cliente:clientes(id, nombre),
      producto:productos(id, nombre),
      empleado:empleados(id, nombre)
    `)
    ?.eq('id', pieza?.trabajo_id)
    ?.single();
  if (trabajoErr) throw trabajoErr;
  if (!trabajo) throw new Error('Trabajo padre no encontrado');

  // 3. Validar que la pieza pertenece al trabajo
  if (pieza?.trabajo_id !== trabajo?.id) {
    throw new Error('La pieza no pertenece al trabajo indicado');
  }

  // 4. Cargar color del cliente desde plan_trabajo_cliente_config
  let clienteColor = null;
  if (trabajo?.cliente_id) {
    try {
      const { data: cfg } = await supabase
        ?.from('plan_trabajo_cliente_config')
        ?.select('color')
        ?.eq('cliente_id', trabajo?.cliente_id)
        ?.maybeSingle();
      clienteColor = cfg?.color || null;
    } catch { /* silent */ }
  }

  // 5. Resolver campos con fallback pieza → trabajo
  const clienteId = trabajo?.cliente_id || null;
  const productoId = pieza?.producto_id || trabajo?.producto_id || null;
  const empleadoId = pieza?.empleado_id || trabajo?.empleado_id || null;
  const tipoCorte = pieza?.tipo_corte || trabajo?.tipo_corte || 'NORMAL';
  const formatoCorte = pieza?.formato_corte || trabajo?.formato_corte || null;
  const quiereHuesos = pieza?.quiere_huesos !== undefined && pieza?.quiere_huesos !== null
    ? pieza?.quiere_huesos
    : (trabajo?.quiere_huesos || false);
  const guardarMerma = pieza?.guardar_merma !== undefined && pieza?.guardar_merma !== null
    ? pieza?.guardar_merma
    : (trabajo?.guardar_merma || false);

  // Instrucciones: observaciones_operario → instrucciones → observaciones
  const instrucciones = pieza?.observaciones_operario
    || trabajo?.instrucciones
    || trabajo?.observaciones
    || '';

  return {
    // IDs para vinculación
    planTrabajoId: trabajo?.id,
    planPiezaId: pieza?.id,

    // Datos resueltos para precargar formulario
    clienteId,
    productoId,
    empleadoId,
    tipoCorte,
    formatoCorte,
    quiereHuesos,
    guardarMerma,
    instrucciones,

    // Objetos completos para mostrar en UI
    pieza,
    trabajo,
    cliente: trabajo?.cliente,
    producto: pieza?.producto || trabajo?.producto,
    empleado: pieza?.empleado || trabajo?.empleado,

    // Color del cliente
    clienteColor,

    // Estado de la pieza
    estadoPieza: pieza?.estado,
    loncheadoId: pieza?.loncheado_id || null,

    // Número de pieza para mostrar
    numeroPieza: pieza?.numero_pieza,
    totalPiezas: trabajo?.cantidad_piezas || 1,
  };
};

// ── SELECTOR: Piezas pendientes/en proceso para Registro de Piezas ────────────
// Devuelve piezas individuales de plan_trabajo_piezas con sus datos de trabajo padre

export const getPiezasPendientesParaSelector = async ({
  incluirCompletadas = false,
  empleadoId = null,
  isAdmin = false,
} = {}) => {
  // Construir query base — incluye color del cliente via plan_trabajo_cliente_config
  let query = supabase
    ?.from('plan_trabajo_piezas')
    ?.select(`
      *,
      producto:productos(id, nombre),
      empleado:empleados(id, nombre),
      trabajo:plan_trabajo(
        id, fecha, estado, cliente_id, producto_id, empleado_id, cantidad_piezas,
        tipo_corte, formato_corte, quiere_huesos, guardar_merma, instrucciones, observaciones,
        orden_dia,
        cliente:clientes(id, nombre),
        producto:productos(id, nombre),
        empleado:empleados(id, nombre),
        cliente_config:plan_trabajo_cliente_config(color)
      )
    `)
    ?.neq('trabajo.estado', 'CANCELADO');

  // Filtrar estados de pieza
  if (incluirCompletadas) {
    query = query?.in('estado', ['PENDIENTE', 'EN_PROCESO', 'INCIDENCIA', 'COMPLETADA']);
  } else {
    query = query?.in('estado', ['PENDIENTE', 'EN_PROCESO', 'INCIDENCIA']);
  }

  query = query?.order('numero_pieza', { ascending: true });

  const { data, error } = await query;
  if (error) { console.error('[getPiezasPendientesParaSelector]', error); throw error; }

  // Filtrar piezas cuyo trabajo padre esté CANCELADO (la query neq no siempre funciona en joins)
  let piezas = (data || [])?.filter(p => p?.trabajo?.estado !== 'CANCELADO' && p?.trabajo !== null);

  // Normalizar color del cliente: puede venir como array o objeto según Supabase
  piezas = piezas?.map(p => {
    const cfg = p?.trabajo?.cliente_config;
    const color = Array.isArray(cfg) ? cfg?.[0]?.color : cfg?.color;
    return {
      ...p,
      trabajo: p?.trabajo ? { ...p?.trabajo, color: color || null } : p?.trabajo,
    };
  });

  // Ordenar: HOY primero → pendientes → en proceso → otras fechas
  // Dentro de cada fecha: respetar orden_dia ASC, luego numero_pieza ASC
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const getPrioridad = (pieza) => {
    const fecha = pieza?.trabajo?.fecha;
    const esHoy = fecha === today;
    const esFutura = fecha > today;
    const esPasada = fecha && fecha < today;

    if (esHoy) {
      if (pieza?.estado === 'PENDIENTE') return 0;
      if (pieza?.estado === 'EN_PROCESO') return 1;
      return 2;
    }
    if (esFutura) return 3;
    if (esPasada) return 4;
    return 5; // sin fecha
  };

  piezas?.sort((a, b) => {
    const pa = getPrioridad(a);
    const pb = getPrioridad(b);
    if (pa !== pb) return pa - pb;
    // Dentro del mismo grupo, ordenar por fecha
    const fa = a?.trabajo?.fecha || '9999';
    const fb = b?.trabajo?.fecha || '9999';
    if (fa !== fb) return fa?.localeCompare(fb);
    // Dentro de la misma fecha, respetar orden_dia
    const oa = a?.trabajo?.orden_dia ?? 9999;
    const ob = b?.trabajo?.orden_dia ?? 9999;
    if (oa !== ob) return oa - ob;
    // Finalmente por numero_pieza
    return (a?.numero_pieza || 0) - (b?.numero_pieza || 0);
  });

  // Si hay empleadoId y no es admin: poner MIS PIEZAS primero
  if (empleadoId && !isAdmin) {
    const misPiezas = piezas?.filter(p => {
      const empPieza = p?.empleado_id;
      const empTrabajo = p?.trabajo?.empleado_id;
      return empPieza === empleadoId || (!empPieza && empTrabajo === empleadoId);
    });
    const otrasPiezas = piezas?.filter(p => {
      const empPieza = p?.empleado_id;
      const empTrabajo = p?.trabajo?.empleado_id;
      return !(empPieza === empleadoId || (!empPieza && empTrabajo === empleadoId));
    });
    piezas = [...misPiezas, ...otrasPiezas];
  }

  return piezas;
};